/**
 * Cost breakdown panel for a run, grouped by source. Plain dollars + percent
 * + units per detail line — no chart deps. See spec § "Component breakdown
 * — RunCostBreakdown". Uses useRunCostEvents (one-shot, terminal-only fetch).
 */
import type { CostEvent, RunResponse } from "@simpliwork/api-schema";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useRunCostEvents } from "@/hooks/use-runs";
import { formatCostMicros } from "@/lib/formatters";

function groupBySource(items: CostEvent[]): Record<string, CostEvent[]> {
  const acc: Record<string, CostEvent[]> = {};
  for (const item of items) {
    const key = item.source;
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
  }
  return acc;
}

function pct(part: number, total: number): string {
  if (total === 0) return "0%";
  return `${Math.round((part / total) * 100)}%`;
}

export function RunCostBreakdown({
  runId,
  runStatus,
}: {
  runId: string;
  runStatus: RunResponse["status"];
}) {
  const query = useRunCostEvents(runId, runStatus);

  return (
    <Card className="border-gray-200">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-gray-700">Cost breakdown</CardTitle>
      </CardHeader>
      <CardContent>
        {!query.data && !query.isError && (
          <p className="text-sm text-muted-foreground">
            Cost events will appear here once the run completes.
          </p>
        )}
        {query.isError && (
          <p className="text-sm text-muted-foreground">Cost details temporarily unavailable.</p>
        )}
        {query.data && query.data.items.length === 0 && (
          <p className="text-sm text-muted-foreground">No cost events for this run.</p>
        )}
        {query.data && query.data.items.length > 0 && <CostBreakdownBody data={query.data} />}
      </CardContent>
    </Card>
  );
}

function CostBreakdownBody({ data }: { data: { items: CostEvent[]; total_cost_micros: number } }) {
  const groups = groupBySource(data.items);
  const total = data.total_cost_micros;

  return (
    <div className="flex flex-col gap-4">
      {Object.entries(groups).map(([source, rows]) => {
        const sourceTotal = rows.reduce((s, r) => s + r.cost_micros, 0);
        return (
          <div key={source}>
            <p className="mb-1 text-sm font-semibold text-gray-700">
              {source} · {formatCostMicros(sourceTotal)} · {pct(sourceTotal, total)}
            </p>
            <ul className="flex flex-col gap-1 pl-3">
              {rows.map((row) => (
                <li
                  key={row.id}
                  className="flex items-center justify-between text-xs text-muted-foreground"
                >
                  <span>
                    {row.detail ?? "(no detail)"} · {row.units} units
                  </span>
                  <span>
                    {formatCostMicros(row.cost_micros)} · {pct(row.cost_micros, total)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
      <div className="flex items-center justify-between border-t border-gray-200 pt-2 text-sm font-semibold">
        <span>Total</span>
        <span>{formatCostMicros(total)}</span>
      </div>
    </div>
  );
}
